import type { SheetSwipeSpec } from './sheetSwipe';

// Ready-made SheetSwipeSpecs for the sheet kinds both apps use (antd 4):
// Modal / Drawer bottom sheets, antd popups restyled as sheets, and custom
// sheets that only need "this is the panel, that is its close button".
// Every dismiss goes through the sheet's own close path, never around it.

type PanelQuery = (panel: HTMLElement) => HTMLElement | null | undefined;

// React's props on a host element (the handlers rc-dialog / rc-drawer attach)
const getReactProps = (el: Element | null | undefined): Record<string, unknown> | undefined => {
  if (!el) return undefined;
  const key = Object.keys(el).find((name) => name.startsWith('__reactProps$'));
  return key ? (el as unknown as Record<string, Record<string, unknown>>)[key] : undefined;
};

// Props of the nearest component above a host element that passes `test`.
const findOwnerProps = (
  el: Element | null | undefined,
  test: (props: Record<string, unknown>) => boolean,
  depth = 6
): Record<string, unknown> | undefined => {
  if (!el) return undefined;
  const key = Object.keys(el).find((name) => name.startsWith('__reactFiber$'));
  type Fiber = { memoizedProps?: Record<string, unknown>; return?: Fiber | null };
  let fiber: Fiber | null | undefined = key
    ? (el as unknown as Record<string, Fiber>)[key]
    : undefined;
  for (let i = 0; fiber && i < depth; i += 1) {
    const props = fiber.memoizedProps;
    if (props && typeof props === 'object' && test(props)) return props;
    fiber = fiber.return;
  }
  return undefined;
};

/** The sheet's own [data-sheet-close] control (not one of a nested sheet). */
const ownCloseControl = (panel: HTMLElement, container: string) =>
  Array.from(panel.querySelectorAll<HTMLElement>('[data-sheet-close]')).find(
    (el) => el.closest(container) === panel
  ) ?? null;

const click = (el: HTMLElement) => {
  el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
};

const pressEscape = (el: HTMLElement) => {
  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    bubbles: true,
    cancelable: true,
  });
  // rc-dialog / rc-drawer read the legacy keyCode
  Object.defineProperty(event, 'keyCode', { get: () => 27 });
  Object.defineProperty(event, 'which', { get: () => 27 });
  el.dispatchEvent(event);
};

const isBlockedByMarkup = (el: HTMLElement) => !!el.closest('[data-sheet-no-swipe]');

// ── antd Modal (rc-dialog) ──────────────────────────────────────────────────
// Panel: .ant-modal inside its .ant-modal-wrap. Dismissable when it shows a
// close button ([data-sheet-close] / .ant-modal-close) or closes on a tap
// outside (maskClosable → the wrap has a click handler). Required / blocking
// dialogs (closable={false} + maskClosable={false}: must-watch video while
// blocking, Modal.confirm decisions…) are never dragged.
const modalWrap = (panel: HTMLElement) =>
  panel.parentElement?.classList.contains('ant-modal-wrap') ? panel.parentElement : null;

const modalCloseButton = (panel: HTMLElement) =>
  ownCloseControl(panel, '.ant-modal') ??
  Array.from(panel.querySelectorAll<HTMLElement>('.ant-modal-close')).find(
    (el) => el.closest('.ant-modal') === panel
  ) ??
  null;

export const antdModalSheet = (
  panel = '.ant-modal-wrap > .ant-modal',
  options: Partial<SheetSwipeSpec> = {}
): SheetSwipeSpec => ({
  panel,
  accept: (el) => !!modalWrap(el) && !isBlockedByMarkup(el),
  backdrop: (el) =>
    modalWrap(el)?.parentElement?.querySelector<HTMLElement>(':scope > .ant-modal-mask'),
  canDismiss: (el) =>
    !!modalCloseButton(el) || typeof getReactProps(modalWrap(el))?.onClick === 'function',
  dismiss: (el) => {
    const button = modalCloseButton(el);
    const wrap = modalWrap(el);
    if (button) click(button);
    // a click on the wrap itself = a tap on the dimmed page (maskClosable)
    else if (wrap) click(wrap);
  },
  ...options,
});

// ── antd Drawer (rc-drawer), placement="bottom" ─────────────────────────────
// Panel: .ant-drawer-content-wrapper. Dismissable through [data-sheet-close],
// the header close button, a closable mask, or Esc (keyboard !== false).
const drawerRoot = (panel: HTMLElement) =>
  panel.parentElement?.classList.contains('ant-drawer') ? panel.parentElement : null;

const drawerCloseButton = (panel: HTMLElement) =>
  ownCloseControl(panel, '.ant-drawer-content-wrapper') ??
  Array.from(panel.querySelectorAll<HTMLElement>('.ant-drawer-close')).find(
    (el) => el.closest('.ant-drawer-content-wrapper') === panel
  ) ??
  null;

const drawerMask = (panel: HTMLElement) =>
  drawerRoot(panel)?.querySelector<HTMLElement>(':scope > .ant-drawer-mask') ?? null;

const drawerPopupProps = (panel: HTMLElement) =>
  findOwnerProps(drawerRoot(panel), (props) => 'placement' in props && 'onClose' in props);

export const antdDrawerSheet = (
  panel = '.ant-drawer-bottom.ant-drawer-open > .ant-drawer-content-wrapper',
  options: Partial<SheetSwipeSpec> = {}
): SheetSwipeSpec => ({
  panel,
  accept: (el) => !!drawerRoot(el) && !isBlockedByMarkup(el),
  backdrop: drawerMask,
  canDismiss: (el) => {
    if (drawerCloseButton(el)) return true;
    if (typeof getReactProps(drawerMask(el))?.onClick === 'function') return true;
    const props = drawerPopupProps(el);
    return typeof props?.onClose === 'function' && props.keyboard !== false;
  },
  dismiss: (el) => {
    const button = drawerCloseButton(el);
    const mask = drawerMask(el);
    const root = drawerRoot(el);
    if (button) click(button);
    else if (mask && typeof getReactProps(mask)?.onClick === 'function') click(mask);
    else if (root) pressEscape(root);
  },
  ...options,
});

// ── antd popups shown as sheets (selects, pickers, dropdown menus,
// pop-confirms) ─────────────────────────────────────────────────────────────
// They close on a mousedown outside them — exactly what a tap on the dimmed
// page does.
export const antdPopupSheet = (
  panel: string,
  options: Partial<SheetSwipeSpec> & { outside?: () => HTMLElement | null } = {}
): SheetSwipeSpec => {
  const { outside, ...rest } = options;
  return {
    panel,
    accept: (el) => !isBlockedByMarkup(el),
    backdrop: () => outside?.() ?? null,
    dismiss: () => {
      const target = outside?.() ?? document.body;
      const init = { bubbles: true, cancelable: true, view: window };
      target.dispatchEvent(new MouseEvent('mousedown', init));
      target.dispatchEvent(new MouseEvent('mouseup', init));
    },
    ...rest,
  };
};

// ── Custom sheets ───────────────────────────────────────────────────────────
// `close`: selector (inside the panel, or a function) of the control that
// closes it — its close button or its backdrop.
export const customSheet = ({
  panel,
  close,
  backdrop,
  ...rest
}: Omit<SheetSwipeSpec, 'dismiss'> & {
  close: string | PanelQuery;
}): SheetSwipeSpec => {
  const control = (el: HTMLElement) =>
    typeof close === 'function' ? close(el) : el.querySelector<HTMLElement>(close);
  return {
    panel,
    backdrop,
    canDismiss: (el) => !isBlockedByMarkup(el) && !!control(el),
    dismiss: (el) => {
      const target = control(el);
      if (target) click(target);
    },
    ...rest,
  };
};

// ── Opt-in markup ───────────────────────────────────────────────────────────
//   <div data-sheet-swipe>            the panel ("handle" → title bar only)
//     <button data-sheet-close>       its close control
//   data-sheet-backdrop="<selector>"  optional: the layer to fade
export const dataAttributeSheet = (): SheetSwipeSpec =>
  customSheet({
    panel: '[data-sheet-swipe]',
    close: (el) => ownCloseControl(el, '[data-sheet-swipe]'),
    backdrop: (el) => {
      const selector = el.getAttribute('data-sheet-backdrop');
      return selector ? document.querySelector<HTMLElement>(selector) : null;
    },
    handleOnly: (el) => el.getAttribute('data-sheet-swipe') === 'handle',
  });
